import { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table';
import { AppTableFeatures } from '~/components/table/appTableFeatures';
import { useMemo, useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '~/helpers/formatCurrency';
import { ConfigProps } from '~/routes/asset-config';
import { AssetConfig } from '~/types';
import { SkeletonElement } from '../skeletons/SkeletonElement';
import { IndeterminateCheckbox, Table } from '../table/Table';

type AssetConfigRow = AssetConfig & { isSkeleton?: boolean };

type AssetCellContext<TValue = unknown> = CellContext<
  AppTableFeatures,
  AssetConfigRow,
  TValue
>;

const getQueuedColor = (queuedType: 'Add' | 'Remove' | undefined) => {
  switch (queuedType) {
    case 'Add':
      return '#00A22B';
    case 'Remove':
      return '#F20505';
    default:
      return undefined;
  }
};

const formatAssetCell = ({ row }: AssetCellContext) => {
  if (row.original?.isSkeleton) {
    return <SkeletonElement />;
  }
  const { asset_name, location_name } = row.original;
  const displayLocation = location_name ? ` - ${location_name}` : '';

  return (
    <div
      style={{
        maxHeight: 30,
        overflow: 'hidden',
      }}
    >
      {asset_name}
      {displayLocation}
    </div>
  );
};

const formatLocationCell =
  (t: TFunction) =>
  ({ row }: AssetCellContext) => {
    if (row.original?.isSkeleton) {
      return <SkeletonElement />;
    }
    const { asset_id, asset_name } = row.original;
    return (
      <div className='flex flex-col truncate'>
        <div className='w-full truncate font-semibold'>{asset_name}</div>
        <div className='text-secondary'>
          {t('type.' + asset_id.split(':')[0])}
        </div>
      </div>
    );
  };

const formatSkuCell =
  (t: TFunction) =>
  ({ row }: AssetCellContext) => {
    if (row.original?.isSkeleton) {
      return <SkeletonElement />;
    }
    const { item_type, sku_id, sku_code, sku_name, queued_type } = row.original;

    if (!sku_id) {
      return <em>empty space</em>;
    }

    return (
      <div className='flex flex-col'>
        <div className='font-semibold'>
          {sku_name}
          {queued_type && (
            <span
              className='w-full truncate'
              style={{ fontWeight: 600, color: getQueuedColor(queued_type) }}
            >
              {' '}
              &#8226; To {queued_type}
            </span>
          )}
        </div>
        <div className='text-secondary'>{t('itemType.' + item_type)}</div>
        {/*  {sku_code && <div className='text-secondary'>{sku_code}</div>} */}
      </div>
    );
  };

const formatIntegerCell = ({ row, getValue }: AssetCellContext) => {
  if (row.original?.isSkeleton) {
    return <SkeletonElement />;
  }
  const value = getValue() as number | null | undefined;
  return value ? value.toLocaleString() : '';
};

function getQueuedColumnId(id: string): keyof AssetConfig | undefined {
  switch (id) {
    case 'price':
      return 'queued_price';
    case 'capacity':
      return 'queued_capacity';
    case 'target':
      return 'queued_target';
  }
  return undefined;
}

const formatQueuableCell = ({ column, row, getValue }: AssetCellContext) => {
  if (row.original?.isSkeleton) {
    return (
      <div className='flex flex-col gap-1'>
        <SkeletonElement />
        <SkeletonElement />
      </div>
    );
  }
  const value = getValue() as number | null | undefined;
  const key = getQueuedColumnId(column.id);

  const formattedValue =
    value || value === 0
      ? column.id === 'price'
        ? formatCurrency(value)
        : value.toLocaleString()
      : '';
  const isAdd = row.original.queued_type === 'Add';
  const queued = key ? row.original[key] : undefined;

  if (!isAdd && queued !== undefined) {
    const formattedQueued =
      queued === null
        ? '—'
        : typeof queued === 'number' && column.id === 'price'
        ? formatCurrency(queued)
        : String(queued);

    return (
      <div style={{ position: 'relative', left: '.5rem' }}>
        <div
          style={{
            height: 30,
            padding: '0 .5rem',
            lineHeight: '30px',
            fontWeight: 600,
            background: 'var(--color-red10)',
            color: '#F20505',
          }}
        >
          {formattedValue || '—'}
        </div>
        <div
          style={{
            height: 30,
            padding: '0 .5rem',
            lineHeight: '30px',
            fontWeight: 600,
            background: 'var(--color-green10)',
            color: '#00A22B',
          }}
        >
          {formattedQueued}
        </div>
      </div>
    );
  }

  return formattedValue;
};

type Props = {
  locationType: ConfigProps['locationType'];
  data: any[];
  selected: any;
  setSelected: any;
  isLoading?: boolean;
};

export const AssetConfigTable = ({
  locationType,
  data,
  selected,
  setSelected,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();

  // If true include target column, false include capacity column
  const [showTarget /* setCapacityTarget */] = useState(true);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Updated skeleton data generation condition
  const skeletonData = useMemo(() => {
    return isLoading // Rely *only* on the isLoading prop passed from parent
      ? Array.from({ length: 15 }, (_, i) => ({
          id: `skeleton-${i}`,
          isSkeleton: true,
        }))
      : [];
  }, [isLoading]);

  // Determine data to pass to the table
  const tableData = skeletonData.length > 0 ? skeletonData : data;

  const locationColumns = useMemo(() => {
    switch (locationType) {
      case 'Asset':
        return [
          {
            header: t('Asset'),
            accessorKey: 'asset_name',
            cell: formatAssetCell,
            size: 150,
          },
        ];
      case 'Site':
        return [
          {
            header: t('siteType'),
            accessorKey: 'site_type',
            size: 80,
          },
          {
            header: t('siteName'),
            accessorKey: 'asset_name',
            size: 170,
          },
        ];
      default: {
        return [
          {
            header: t('location'),
            accessorKey: 'asset_name',
            cell: formatLocationCell(t),
            size: 150,
          },
        ];
      }
    }
  }, [locationType, t]);

  const columns = useMemo(
    (): ColumnDef<AppTableFeatures, AssetConfigRow>[] => [
      {
        id: 'select',
        header: ({
          table,
        }: HeaderContext<AppTableFeatures, AssetConfigRow>) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }: AssetCellContext) => (
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        ),
        size: 30,
        minSize: 30,
      },
      ...locationColumns,
      {
        header: t('space'),
        accessorKey: 'space_name',
        size: 90,
      },
      {
        header: t('itemName'),
        accessorKey: 'sku_name',
        cell: formatSkuCell(t),
        size: 300,
      },
      /*       {
        header: t('itemTypeLabel'),
        accessorKey: 'item_type',
        cell: ({ row }: CellContext<AssetConfig, any>) => {
          return row.original.item_type
            ? t('itemType.' + row.original.item_type)
            : '';
        },
        size: 120,
      }, */
      {
        header: t('price'),
        accessorKey: 'price',
        meta: { align: 'right' as const },
        cell: formatQueuableCell,
        size: 80,
      },
      ...(showTarget
        ? [
            {
              header: t('target'),
              accessorKey: 'target',
              meta: { align: 'right' as const },
              cell: formatQueuableCell,
              size: 80,
            },
          ]
        : [
            {
              header: t('capacity'),
              accessorKey: 'capacity',
              meta: { align: 'right' as const },
              cell: formatQueuableCell,
              size: 80,
            },
          ]),
      /*     {
        header: 'Empty',
        accessorKey: 'empty',
        meta: { align: 'right' as const },
        cell: formatIntegerCell,
        size:80,
      }, */
      {
        header: t('On Hand'),
        accessorKey: 'soh',
        meta: { align: 'right' as const },
        cell: formatIntegerCell,
        size: 80,
      },
      {
        header: t('missing'),
        accessorKey: showTarget ? 'missing' : 'missing_capacity',
        meta: { align: 'right' as const },
        cell: formatIntegerCell,
        size: 80,
      },
      {
        header: t('reorderAt'),
        accessorKey: 'reorder_level',
        meta: { align: 'right' as const },
        cell: formatIntegerCell,
        size: 100,
      },
    ],
    [t, locationColumns, showTarget]
  );

  const sortBy = useMemo(
    () => [
      { id: 'asset_name', desc: false },
      { id: 'space_name', desc: false },
    ],
    []
  );

  const handleRowSelect = (selectedRowPaths: Record<string, boolean>) => {
    setSelected(selectedRowPaths);
  };

  return (
    <div ref={tableContainerRef} className='h-full flex-grow overflow-auto'>
      <Table
        columns={columns}
        data={tableData}
        containerRef={tableContainerRef}
        initialSelectedRowIds={selected}
        initialSortBy={sortBy}
        onRowSelect={handleRowSelect}
        isLoading={isLoading}
        style={{ flexGrow: 1 }}
      />
    </div>
  );
};
